/**
 * Stock Price Service
 * Fetches live stock prices and search from Yahoo Finance
 * Uses Netlify serverless function as proxy to bypass CORS
 */

const PROXY_URL = '/.netlify/functions/yahoo-finance';

// Cache for prices (avoid hitting API too often)
const priceCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Call the Yahoo Finance proxy function
 */
const callProxy = async (body) => {
  try {
    const response = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.warn(`Proxy error: ${response.status}`);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Proxy call failed:', error);
    return null;
  }
};

/**
 * Fetch current price AND previous close for a single stock.
 * Used by Top Movers to calculate day change without depending on nightly job.
 * @returns {Promise<{currentPrice: number, previousClose: number}>}
 */
export const fetchStockPriceWithPrevClose = async (symbol, assetType) => {
  if (!symbol) return { currentPrice: 0, previousClose: 0 };

  const upperSymbol = symbol.toUpperCase();
  let yahooSymbol;

  // If the symbol already carries an exchange suffix (.NS / .BO — set by the nightly
  // job via stock_isin_mapping), use it directly.  Only append .NS when the symbol
  // is bare AND the asset type signals an Indian stock.
  if (upperSymbol.includes('.')) {
    yahooSymbol = upperSymbol;
  } else if (assetType === 'STOCK_INDIAN') {
    yahooSymbol = `${upperSymbol}.NS`;
  } else {
    // STOCK_US or generic STOCK (bare symbol) — US tickers have no exchange suffix
    yahooSymbol = upperSymbol;
  }

  const tryFetch = async (sym) => {
    const data = await callProxy({ action: 'chart', symbol: sym });
    const meta = data?.chart?.result?.[0]?.meta;
    if (!meta) return null;
    return {
      currentPrice: meta.regularMarketPrice || 0,
      previousClose: meta.chartPreviousClose || meta.previousClose || 0,
    };
  };

  let result = await tryFetch(yahooSymbol);

  // BSE fallback for Indian stocks — works for both STOCK_INDIAN and generic STOCK
  // holdings whose yahoo_symbol resolved to a .NS suffix.
  const isIndianSymbol = assetType === 'STOCK_INDIAN' || yahooSymbol.endsWith('.NS');
  if ((!result || result.currentPrice === 0) && isIndianSymbol) {
    const baseSymbol = upperSymbol.replace(/\.NS$/i, '');
    result = await tryFetch(`${baseSymbol}.BO`);
  }

  return result || { currentPrice: 0, previousClose: 0 };
};

/**
 * Fetch current price for a single stock
 * @param {string} symbol - Stock symbol (e.g., RELIANCE, AAPL)
 * @param {string} assetType - STOCK_INDIAN or STOCK_US
 * @returns {Promise<number>} Current price (0 if fetch fails)
 */
export const fetchStockPrice = async (symbol, assetType) => {
  if (!symbol) return 0;

  // Check cache first
  const cacheKey = `${symbol}_${assetType}`;
  const cached = priceCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.price;
  }

  try {
    // Add exchange suffix for Indian stocks
    let yahooSymbol = symbol.toUpperCase();
    if (assetType === 'STOCK_INDIAN') {
      yahooSymbol = `${symbol}.NS`; // NSE suffix
    }
    // US stocks don't need suffix

    const data = await callProxy({ action: 'chart', symbol: yahooSymbol });

    if (data) {
      const price = data?.chart?.result?.[0]?.meta?.regularMarketPrice;
      if (price) {
        priceCache.set(cacheKey, { price, timestamp: Date.now() });
        return price;
      }
    }

    // Try BSE for Indian stocks if NSE fails
    if (assetType === 'STOCK_INDIAN') {
      const bseData = await callProxy({ action: 'chart', symbol: `${symbol}.BO` });
      if (bseData) {
        const bsePrice = bseData?.chart?.result?.[0]?.meta?.regularMarketPrice;
        if (bsePrice) {
          priceCache.set(cacheKey, { price: bsePrice, timestamp: Date.now() });
          return bsePrice;
        }
      }
    }

    console.warn(`Failed to fetch price for ${symbol}`);
    return 0;
  } catch (error) {
    console.error(`Error fetching price for ${symbol}:`, error);
    return 0;
  }
};

/**
 * Fetch USD to INR exchange rate
 * @returns {Promise<number>} Exchange rate (defaults to 96.5 if fetch fails)
 */
export const fetchUSDtoINR = async () => {
  const cacheKey = 'USDINR';
  const cached = priceCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.price;
  }

  try {
    const data = await callProxy({ action: 'forex' });

    if (data) {
      const rate = data?.chart?.result?.[0]?.meta?.regularMarketPrice;
      if (rate) {
        priceCache.set(cacheKey, { price: rate, timestamp: Date.now() });
        return rate;
      }
    }

    console.warn('Failed to fetch USD/INR rate, using default');
    return 96.5;
  } catch (error) {
    console.error('Error fetching USD/INR rate:', error);
    return 96.5;
  }
};

/**
 * Fetch prices for multiple stocks
 * @param {Array} stocks - Array of { symbol, assetType }
 * @returns {Promise<Object>} Map of symbol -> price
 */
export const fetchMultipleStockPrices = async (stocks) => {
  const prices = {};

  for (const { symbol, assetType } of stocks) {
    if (symbol) {
      prices[symbol] = await fetchStockPrice(symbol, assetType);
      // Small delay to avoid rate limiting
      await new Promise((r) => setTimeout(r, 100));
    }
  }

  return prices;
};

/**
 * Fetch stock price and convert to INR if needed
 * @param {string} symbol - Stock symbol
 * @param {string} assetType - STOCK_INDIAN or STOCK_US
 * @returns {Promise<{priceInOriginal: number, priceInINR: number, exchangeRate: number}>}
 */
export const fetchStockPriceInINR = async (symbol, assetType) => {
  const priceInOriginal = await fetchStockPrice(symbol, assetType);

  if (assetType === 'STOCK_US') {
    const exchangeRate = await fetchUSDtoINR();
    return {
      priceInOriginal,
      priceInINR: priceInOriginal * exchangeRate,
      exchangeRate,
    };
  }

  return {
    priceInOriginal,
    priceInINR: priceInOriginal,
    exchangeRate: 1,
  };
};

/**
 * Search for stocks using Yahoo Finance (via proxy)
 * @param {string} query - Search query (e.g., "rel" for Reliance)
 * @param {string} market - 'indian' or 'us'
 * @returns {Promise<Array>} Array of matching stocks
 */
export const searchStocks = async (query, market = 'indian') => {
  if (!query || query.length < 2) return [];

  try {
    const data = await callProxy({ action: 'search', query });

    if (!data || !data.quotes) return [];

    // Filter by market
    const filtered = data.quotes.filter((q) => {
      if (!q.symbol) return false;

      if (market === 'indian') {
        // Indian stocks end with .NS (NSE) or .BO (BSE)
        return q.symbol.endsWith('.NS') || q.symbol.endsWith('.BO');
      } else {
        // US stocks - NASDAQ, NYSE, etc.
        return (
          ['NMS', 'NYQ', 'NASDAQ', 'NYSE', 'NGM', 'PCX', 'NCM', 'NAS'].includes(q.exchange) ||
          (!q.symbol.includes('.') && q.quoteType === 'EQUITY')
        );
      }
    });

    // Map to clean format
    return filtered.map((q) => ({
      symbol: q.symbol.replace('.NS', '').replace('.BO', ''), // Clean symbol for display
      yahooSymbol: q.symbol, // Keep full symbol for price fetch
      name: q.shortname || q.longname || q.symbol,
      exchange:
        q.exchange || (q.symbol.endsWith('.NS') ? 'NSE' : q.symbol.endsWith('.BO') ? 'BSE' : 'US'),
      type: q.quoteType,
    }));
  } catch (error) {
    console.error('Stock search error:', error);
    return [];
  }
};

/**
 * Fetch price using Yahoo symbol directly (for validated stocks)
 * @param {string} yahooSymbol - Full Yahoo symbol (e.g., RELIANCE.NS, AAPL)
 * @returns {Promise<number>} Current price
 */
export const fetchPriceByYahooSymbol = async (yahooSymbol) => {
  if (!yahooSymbol) return 0;

  const cacheKey = `yahoo_${yahooSymbol}`;
  const cached = priceCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.price;
  }

  try {
    const data = await callProxy({ action: 'chart', symbol: yahooSymbol });

    if (data) {
      const price = data?.chart?.result?.[0]?.meta?.regularMarketPrice;
      if (price) {
        priceCache.set(cacheKey, { price, timestamp: Date.now() });
        return price;
      }
    }

    console.warn(`Failed to fetch price for ${yahooSymbol}`);
    return 0;
  } catch (error) {
    console.error(`Error fetching price for ${yahooSymbol}:`, error);
    return 0;
  }
};

/**
 * Clear the price cache (useful for forcing refresh)
 */
export const clearPriceCache = () => {
  priceCache.clear();
};
